"use client";

// ── Run samples and custom input: the buttons, the panel, the strip (`EX-EDR02`)
// Wave 23 decision 6, boards RunSamples, CustomRun and RunBusy. Three pieces,
// placed by `GradedEditorColumn`:
//
//   - the two bar buttons, in its `runSlot` beside Submit;
//   - the panel, between the editor and the strip. It pushes the editor rather
//     than floating over it, because a comparison is read beside the code it
//     describes; it is resizable, closable, and its height is remembered;
//   - the strip, in the strip's row, describing the last sample run until the
//     next submission takes the row back.
//
// Nothing here is a mark: a run is never a hidden test, never counted, never a
// penalty, and the copy says so wherever a result is shown.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown, ChevronUp, Clock, Loader2, Lock, Play, SquareTerminal, X } from "lucide-react";
import { useTranslations } from "next-intl";

import { runCompileFeedback } from "./compile-diagnostics";
import type { RunTrack } from "./use-code-runs";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Textarea } from "@/components/ui/textarea";
import type { SampleRunCaseResult } from "@/features/quiz/coding-task-api";
import styles from "./test-strip.module.css";
import { formatMegabytes } from "./TestStrip";
import {
  firstDifferingLine,
  firstLineOf,
  isBadRunCase,
  outputLines,
  runCaseStateOf,
  type RunCaseState,
} from "./run-output";
import { RunWaitClock } from "./RunWaitClock";
import { isRunShortcut, RUN_STDIN_MAX, type CodeRuns, type RunTab } from "./use-code-runs";
import { RunTwiceStages } from "./RunTwiceStages";
import { InteractionTranscript } from "./InteractionTranscript";

export const RUN_PANEL_ID = "code-run-panel";
const CUSTOM_STDIN_ID = "code-run-stdin";

/** A sample as the statement shows it. */
export type RunSample = { input: string; output: string };

/** The backend runs at most five samples (`SAMPLE_RUN_MAX_CASES`). */
export const RUN_SAMPLE_MAX = 5;

const HEIGHT_KEY = "progsity:judge:run-panel-height";
const DEFAULT_HEIGHT = 262;
const MIN_HEIGHT = 150;
const MAX_HEIGHT = 640;
const HEIGHT_STEP = 24;

function clampHeight(value: number): number {
  return Math.round(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, value)));
}

function loadHeight(): number {
  if (typeof window === "undefined") return DEFAULT_HEIGHT;
  try {
    const stored = Number(window.localStorage.getItem(HEIGHT_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampHeight(stored) : DEFAULT_HEIGHT;
  } catch {
    return DEFAULT_HEIGHT;
  }
}

function saveHeight(value: number): void {
  try {
    window.localStorage.setItem(HEIGHT_KEY, String(value));
  } catch {
    /* a blocked store keeps the default next time */
  }
}

const CASE_TONE: Readonly<Record<RunCaseState, string>> = {
  match: "bg-emerald-400/15 text-emerald-300",
  differ: "bg-red-400/15 text-red-300",
  failed: "bg-red-400/15 text-red-300",
  unchecked: "bg-white/10 text-white/80",
};

function CaseIcon({ state }: { state: RunCaseState | null }) {
  if (state === "match") return <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />;
  if (state === "differ" || state === "failed") return <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />;
  return null;
}

/* ── The bar's two buttons ───────────────────────────────────────────────── */

export function RunSlotButtons({
  runs,
  disabled,
  hasSamples,
}: {
  runs: CodeRuns;
  /** No draft yet, or no runtime to run it on. */
  disabled: boolean;
  hasSamples: boolean;
}) {
  const t = useTranslations("judge.run");
  const running = runs.samples.phase === "running";

  return (
    <>
      <Button
        type="button"
        variant="outlineDark"
        size="pill"
        onClick={runs.runSamples}
        disabled={disabled || !hasSamples || running}
        title={hasSamples ? t("runSamplesTitle") : t("noSamples")}
      >
        {running ? (
          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
        ) : (
          <Play className="h-4 w-4" aria-hidden />
        )}
        <span className="sr-only sm:not-sr-only">{t("runSamples")}</span>
      </Button>
      <Button
        type="button"
        variant="outlineDark"
        size="pill"
        onClick={runs.toggleCustom}
        disabled={disabled}
        aria-pressed={runs.panel === "custom"}
        aria-controls={RUN_PANEL_ID}
      >
        <SquareTerminal className="h-4 w-4" aria-hidden />
        <span className="sr-only sm:not-sr-only">{t("customInput")}</span>
      </Button>
    </>
  );
}

/* ── The panel ───────────────────────────────────────────────────────────── */

function OutputBox({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-1">
      <p className="text-[11px] font-semibold text-white/50">{label}</p>
      <div
        className="min-h-[3.25rem] flex-1 overflow-auto rounded-lg bg-neutral-950 px-2 py-1.5 font-mono text-xs leading-5 text-white/85 ring-1 ring-inset ring-white/10"
        {...(testId ? { "data-testid": testId } : {})}
      >
        {children}
      </div>
    </div>
  );
}

function Lines({ text, mark }: { text: string; mark: number | null }) {
  const lines = outputLines(text);
  // A differing line past the end of this side (the program printed less, or
  // more, than expected) is marked on the side that has it.
  return (
    <>
      {lines.map((line, i) => (
        <div
          key={i}
          className={cn("whitespace-pre", i === mark && "-mx-2 bg-red-400/15 px-2 text-red-200")}
          {...(i === mark ? { "data-differs": "true" } : {})}
        >
          {line || " "}
        </div>
      ))}
    </>
  );
}

function Quiet({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "error" }) {
  return (
    <p
      className={cn(
        "font-sans text-xs",
        tone === "error" ? "font-semibold text-red-300" : "text-white/45",
      )}
    >
      {children}
    </p>
  );
}

/** `EX-SRN01`: a run that waited a full minute for its turn. Calm, never "busy". */
function SlowNote({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations("judge.run");
  return (
    <div className="flex items-start gap-2.5 font-sans" role="status">
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden />
      <p className="min-w-0 flex-1 text-xs leading-snug">
        <span className="font-semibold text-white">{t("slowTitle")}</span>{" "}
        <span className="text-white/60">{t("slowBody")}</span>
      </p>
      <Button type="button" variant="inverse" size="mini" onClick={onRetry}>
        {t("tryAgain")}
      </Button>
    </div>
  );
}

type Translate = ReturnType<typeof useTranslations>;

function figuresOf(result: SampleRunCaseResult, locale: string, tEditor: Translate): string[] {
  return [
    result.timeMs != null ? tEditor("figureMs", { ms: result.timeMs }) : null,
    result.memoryKb != null ? tEditor("figureMb", { mb: formatMegabytes(result.memoryKb, locale) }) : null,
  ].filter((part): part is string => part !== null);
}

function CompileRunLink({ track, onGoToLine }: { track: RunTrack; onGoToLine?: (line: number, column: number) => void }) {
  const t = useTranslations("judge.editor");
  const first = runCompileFeedback(track).errors[0];
  return first && onGoToLine ? (
    <Button type="button" variant="linkDark" size="sm" onClick={() => onGoToLine(first.line, first.column)}>
      {t("goToLine", { n: first.line })}
    </Button>
  ) : null;
}

function SamplesTab({
  runs,
  samples,
  locale,
  onGoToLine,
  runTwice,
  interactive = false,
}: {
  runs: CodeRuns;
  samples: RunSample[];
  locale: string;
  onGoToLine?: (line: number, column: number) => void;
  runTwice: boolean;
  /** `EX-IAC01`: each case is a conversation with the judge's interactor. */
  interactive?: boolean;
}) {
  const t = useTranslations("judge.run");
  const tProblem = useTranslations("judge.problem");
  const tEditor = useTranslations("judge.editor");
  const { phase, result } = runs.samples;

  if (samples.length === 0) return <Quiet>{t("noSamples")}</Quiet>;

  if (phase === "slow") return <SlowNote onRetry={runs.runSamples} />;
  if (phase === "failed") return <Quiet tone="error">{runs.samples.error}</Quiet>;

  if (phase === "done" && result && !result.compiled) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-1">
        <p className="text-xs font-semibold text-red-300">{t("doesNotCompile")}</p>
        <pre
          className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-lg bg-neutral-950 p-2 font-mono text-[11px] leading-5 text-red-200/90 ring-1 ring-inset ring-white/10"
          data-testid="run-compile-output"
        >
          {result.compileOutput || tProblem("runCompileErrorEmpty")}
        </pre>
        <CompileRunLink track={runs.samples} onGoToLine={onGoToLine} />
      </div>
    );
  }

  const index = Math.min(runs.selected, samples.length - 1);
  const sample = samples[index];
  const caseResult = result?.results[index] ?? null;
  const state = caseResult ? runCaseStateOf(caseResult) : null;
  const mark = caseResult && state === "differ" ? firstDifferingLine(sample.output, caseResult.stdout) : null;
  const meta = caseResult
    ? [t("sampleN", { n: index + 1 }), ...figuresOf(caseResult, locale, tEditor)].join(" · ")
    : t("samplesFromStatement");

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        {samples.map((_, i) => {
          const row = result?.results[i];
          const rowState = row ? runCaseStateOf(row) : null;
          return (
            <Button
              key={i}
              type="button"
              variant={rowState ? null : "ghostDark"}
              size="mini"
              aria-pressed={index === i}
              onClick={() => runs.openSample(i)}
              className={cn(
                "shrink-0",
                rowState && CASE_TONE[rowState],
                rowState && index === i && "ring-1 ring-inset ring-current",
              )}
            >
              <CaseIcon state={rowState} />
              {t("sampleN", { n: i + 1 })}
            </Button>
          );
        })}
        <span className="ml-auto min-w-0 truncate pl-2 text-[11px] text-white/45">{meta}</span>
      </div>

      {interactive ? (
        // `EX-IAC01`: the conversation in place of the three boxes.
        <InteractiveCase result={caseResult} running={phase === "running"} startedAt={runs.samples.startedAt} />
      ) : runTwice ? (
        // `EX-RTW03`, board E: run 1, the jury's check and run 2 in place of the three boxes.
        <RunTwiceStages
          result={caseResult}
          running={phase === "running"}
          input={sample.input}
          expected={sample.output}
          layout="row"
          locale={locale}
        />
      ) : (
      <div className="grid min-h-0 flex-1 gap-2 sm:grid-cols-3">
        <OutputBox label={t("input")}>
          <Lines text={sample.input} mark={null} />
        </OutputBox>
        <OutputBox label={t("expected")}>
          <Lines text={sample.output} mark={mark !== null && mark < outputLines(sample.output).length ? mark : null} />
        </OutputBox>
        <OutputBox label={t("yours")} testId="run-your-output">
          {phase === "running" ? (
            <RunWaitClock
              since={runs.samples.startedAt}
              label={t("runningOnJudge")}
              running={(seconds) => t("runningSeconds", { seconds })}
            />
          ) : !caseResult ? (
            <Quiet>{t("pressRunSamples")}</Quiet>
          ) : (
            <>
              {state === "failed" ? (
                <Quiet tone="error">{tProblem(`runOutcome_${caseResult.outcome}`)}</Quiet>
              ) : null}
              {caseResult.stdout.trim() === "" ? (
                state === "failed" ? null : <Quiet tone="error">{t("printedNothing")}</Quiet>
              ) : (
                <Lines text={caseResult.stdout} mark={mark} />
              )}
            </>
          )}
        </OutputBox>
      </div>
      )}

      {caseResult?.stderr && !runTwice ? (
        <OutputBox label={t("stderr")}>
          <span className="whitespace-pre-wrap text-red-200/90">{caseResult.stderr}</span>
        </OutputBox>
      ) : null}
      {caseResult?.outputTruncated ? <Quiet>{tProblem("runOutputTruncated")}</Quiet> : null}
    </div>
  );
}

function CustomTab({
  runs,
  samples,
  locale,
  onGoToLine,
  runTwice,
  interactive = false,
}: {
  runs: CodeRuns;
  samples: RunSample[];
  locale: string;
  onGoToLine?: (line: number, column: number) => void;
  runTwice: boolean;
  /** `EX-IAC01`: no custom input where the judge holds the hidden answer. */
  interactive?: boolean;
}) {
  if (interactive) return <NoCustomInput onSamples={() => runs.setPanel("samples")} />;
  return <CustomTabBody runs={runs} samples={samples} locale={locale} onGoToLine={onGoToLine} runTwice={runTwice} />;
}

function CustomTabBody({
  runs,
  samples,
  locale,
  onGoToLine,
  runTwice,
}: {
  runs: CodeRuns;
  samples: RunSample[];
  locale: string;
  onGoToLine?: (line: number, column: number) => void;
  runTwice: boolean;
}) {
  const t = useTranslations("judge.run");
  const tProblem = useTranslations("judge.problem");
  const tEditor = useTranslations("judge.editor");
  const { phase, result } = runs.custom;
  const caseResult = result?.compiled ? (result.results[0] ?? null) : null;
  const running = phase === "running";

  const meta =
    phase === "done" && caseResult
      ? [...figuresOf(caseResult, locale, tEditor), tProblem(`runOutcome_${caseResult.outcome}`)].join(" · ")
      : "";

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (isRunShortcut(event)) {
      event.preventDefault();
      runs.runCustom();
    }
  };

  return (
    <div className="grid h-full min-h-0 gap-3 sm:grid-cols-2">
      <div className="flex min-h-0 min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <label htmlFor={CUSTOM_STDIN_ID} className="text-[11px] font-semibold text-white/50">
            {t("input")}
          </label>
          {samples.length > 0 ? (
            <>
              <span className="ml-auto shrink-0 text-[11px] text-white/40">{t("fillWith")}</span>
              {samples.map((sample, i) => (
                <Button
                  key={i}
                  type="button"
                  variant="ghostDark"
                  size="mini"
                  onClick={() => runs.setStdin(sample.input)}
                  className="shrink-0"
                >
                  {t("sampleN", { n: i + 1 })}
                </Button>
              ))}
            </>
          ) : null}
        </div>
        <Textarea
          id={CUSTOM_STDIN_ID}
          tone="dark"
          value={runs.stdin}
          maxLength={RUN_STDIN_MAX}
          spellCheck={false}
          onChange={(event) => runs.setStdin(event.target.value)}
          onKeyDown={onKeyDown}
          className="min-h-[3.25rem] flex-1 resize-none rounded-lg px-2 py-1.5 font-mono text-xs leading-5"
        />
      </div>

      <div className="flex min-h-0 min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="text-[11px] font-semibold text-white/50">{t("output")}</p>
          <span className="min-w-0 truncate text-[11px] text-white/45" data-testid="run-custom-meta">
            {meta}
          </span>
          <Button
            type="button"
            variant="inverse"
            size="mini"
            onClick={runs.runCustom}
            disabled={running}
            title={t("runCustomTitle")}
            className="ml-auto shrink-0"
          >
            {running ? (
              <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden />
            )}
            {t("run")}
            <kbd aria-hidden className="hidden font-sans text-[10px] text-neutral-500 sm:inline">
              {t("runShortcut")}
            </kbd>
          </Button>
        </div>
        <div
          className="min-h-[3.25rem] flex-1 overflow-auto rounded-lg bg-neutral-950 px-2 py-1.5 font-mono text-xs leading-5 text-white/85 ring-1 ring-inset ring-white/10"
          role="status"
          aria-live="polite"
          data-testid="run-custom-output"
        >
          {phase === "idle" ? (
            <Quiet>{t("pressRunCustom")}</Quiet>
          ) : running ? (
            <RunWaitClock
              since={runs.custom.startedAt}
              label={t("runningOnJudge")}
              running={(seconds) => t("runningSeconds", { seconds })}
              inLine={t("runningInLine")}
            />
          ) : phase === "slow" ? (
            <SlowNote onRetry={runs.runCustom} />
          ) : phase === "failed" ? (
            <Quiet tone="error">{runs.custom.error}</Quiet>
          ) : result && !result.compiled ? (
            <>
              <Quiet tone="error">{t("doesNotCompileNothingRan")}</Quiet>
              <pre className="mt-1 whitespace-pre-wrap text-[11px] text-red-200/90">
                {result.compileOutput || tProblem("runCompileErrorEmpty")}
              </pre>
              <CompileRunLink track={runs.custom} onGoToLine={onGoToLine} />
            </>
          ) : caseResult && runTwice && caseResult.stages ? (
            // `EX-RTW03`, board E: the same three stages for the reader's own input.
            <RunTwiceStages
              result={caseResult}
              running={false}
              input={null}
              expected={null}
              layout="stack"
              locale={locale}
            />
          ) : caseResult ? (
            <>
              {caseResult.stdout === "" ? (
                <Quiet tone="error">{t("printedNothing")}</Quiet>
              ) : (
                <div className="whitespace-pre">{caseResult.stdout}</div>
              )}
              {caseResult.stderr ? (
                <div className="mt-1 whitespace-pre-wrap text-red-200/90">{caseResult.stderr}</div>
              ) : null}
              {caseResult.outputTruncated ? <Quiet>{tProblem("runOutputTruncated")}</Quiet> : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The one line beside the tabs: what the panel is showing, in a sentence. */
function panelNote(
  runs: CodeRuns,
  samples: RunSample[],
  t: Translate,
  tTwice: Translate,
): { text: string; tone: "muted" | "good" | "bad" } {
  if (runs.panel === "custom") return { text: t("customNote"), tone: "muted" };
  const result = runs.samples.result;
  if (runs.samples.phase !== "done" || !result || !result.compiled || samples.length === 0) {
    return { text: t("noteIdle"), tone: "muted" };
  }
  const index = Math.min(runs.selected, samples.length - 1);
  const row = result.results[index];
  if (!row) return { text: t("noteIdle"), tone: "muted" };
  const n = index + 1;
  // `EX-RTW03`: a case that stopped at the jury's check printed run 1's output
  // only, which is not the sample's expected output; say what happened instead.
  if (row.stage === "bridge") {
    return row.stages?.bridge?.status === "error"
      ? { text: tTwice("noteBridgeError", { n }), tone: "muted" }
      : { text: tTwice("noteBridge", { n }), tone: "bad" };
  }
  switch (runCaseStateOf(row)) {
    case "match":
      return { text: t("noteMatch", { n }), tone: "good" };
    case "unchecked":
      return { text: t("noteUnchecked"), tone: "muted" };
    case "failed":
      return { text: t("noteFailed", { n }), tone: "bad" };
    case "differ": {
      if (row.stdout.trim() === "") return { text: t("noteNothing", { n }), tone: "bad" };
      const line = firstDifferingLine(samples[index].output, row.stdout);
      return line === null
        ? { text: t("noteDiffers", { n }), tone: "bad" }
        : { text: t("noteLineDiffers", { n, line: line + 1 }), tone: "bad" };
    }
  }
}

export function RunPanel({
  runs,
  samples,
  locale,
  onGoToLine,
  runTwice = false,
  interactive = false,
}: {
  runs: CodeRuns;
  samples: RunSample[];
  locale: string;
  onGoToLine?: (line: number, column: number) => void;
  /** `EX-RTW03`: a run-twice problem draws each case as its three stages. */
  runTwice?: boolean;
  /** `EX-IAC01`: an interactive problem draws each case as its conversation, and has no custom input. */
  interactive?: boolean;
}) {
  const t = useTranslations("judge.run");
  const tTwice = useTranslations("judge.runTwice");
  // Mounted only once the reader opens it, so reading the store here never
  // meets a server render.
  const [height, setHeight] = useState(loadHeight);
  const heightRef = useRef(height);
  heightRef.current = height;
  const dragRef = useRef<{ y: number; height: number } | null>(null);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!dragRef.current) return;
      setHeight(clampHeight(dragRef.current.height + dragRef.current.y - event.clientY));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      saveHeight(heightRef.current);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragRef.current = { y: event.clientY, height: heightRef.current };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const onSeparatorKey = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    // Up grows the panel, the way dragging the handle up does.
    const delta = event.key === "ArrowUp" ? HEIGHT_STEP : event.key === "ArrowDown" ? -HEIGHT_STEP : 0;
    if (!delta) return;
    event.preventDefault();
    const next = clampHeight(heightRef.current + delta);
    setHeight(next);
    saveHeight(next);
  }, []);

  const resetHeight = useCallback(() => {
    setHeight(DEFAULT_HEIGHT);
    saveHeight(DEFAULT_HEIGHT);
  }, []);

  if (!runs.panel) return null;
  const tab: RunTab = runs.panel;
  const note = panelNote(runs, samples, t, tTwice);

  return (
    <section
      id={RUN_PANEL_ID}
      aria-label={t("panelLabel")}
      className="relative flex max-h-[70%] min-h-0 shrink-0 flex-col border-t border-white/10 bg-neutral-900"
      style={{ height }}
      data-testid="run-panel"
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={t("resizePanel")}
        aria-valuemin={MIN_HEIGHT}
        aria-valuemax={MAX_HEIGHT}
        aria-valuenow={height}
        tabIndex={0}
        title={t("resizeHint")}
        onPointerDown={onPointerDown}
        onKeyDown={onSeparatorKey}
        onDoubleClick={resetHeight}
        className="group absolute inset-x-0 -top-1.5 z-10 flex h-3 cursor-row-resize touch-none select-none items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span
          className="h-1 w-10 rounded-full bg-white/15 transition-colors duration-150 ease-out group-hover:bg-white/30 group-active:bg-primary"
          aria-hidden
        />
      </div>

      <div className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-2.5">
        <SegmentedControl
          tone="dark"
          size="sm"
          label={t("tabsLabel")}
          options={[
            { value: "samples", label: t("tabSamples") },
            { value: "custom", label: t("tabCustom") },
          ]}
          value={tab}
          onChange={runs.setPanel}
          className="shrink-0"
        />
        <p
          className={cn(
            "min-w-0 flex-1 truncate text-xs",
            note.tone === "good" && "font-semibold text-emerald-300",
            note.tone === "bad" && "font-semibold text-red-300",
            note.tone === "muted" && "text-white/45",
          )}
          data-testid="run-panel-note"
        >
          {note.text}
        </p>
        <Button
          type="button"
          variant="ghostDark"
          size="miniIcon"
          onClick={runs.closePanel}
          aria-label={t("closePanel")}
          className="shrink-0"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {tab === "samples" ? (
          <SamplesTab runs={runs} samples={samples} locale={locale} onGoToLine={onGoToLine} runTwice={runTwice} interactive={interactive} />
        ) : (
          <CustomTab runs={runs} samples={samples} locale={locale} onGoToLine={onGoToLine} runTwice={runTwice} interactive={interactive} />
        )}
      </div>
    </section>
  );
}

/* ── Interactive problems (`EX-IAC01`) ───────────────────────────────────── */

/** One sample's conversation and the interactor's verdict, the contestant board's run panel. */
function InteractiveCase({
  result,
  running,
  startedAt,
}: {
  result: SampleRunCaseResult | null;
  running: boolean;
  startedAt: number | undefined;
}) {
  const t = useTranslations("judge.run");
  const tInteractive = useTranslations("judge.interactive");
  if (running) {
    return (
      <div className="rounded-xl bg-neutral-950 p-3 ring-1 ring-inset ring-white/10">
        <RunWaitClock since={startedAt} label={t("runningOnJudge")} running={(seconds) => t("runningSeconds", { seconds })} />
      </div>
    );
  }
  const interaction = result?.interaction;
  if (!interaction) return <Quiet>{t("pressRunSamples")}</Quiet>;
  const accepted = interaction.verdict === "accepted";
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid="interactive-case">
      <p className={cn("text-xs font-semibold", accepted ? "text-emerald-300" : "text-red-300")} data-testid="interactive-verdict">
        {interaction.idle ? tInteractive("idle") : tInteractive(`verdict_${interaction.verdict}`)}
      </p>
      <InteractionTranscript
        entries={interaction.transcript}
        truncated={interaction.transcriptTruncated}
        ended={accepted ? tInteractive("endedAccepted") : null}
      />
      {interaction.idle ? <Quiet tone="error">{tInteractive("idleWhy")}</Quiet> : null}
    </div>
  );
}

/** Where custom input would be: the reason, in one sentence, and the way back to the samples. */
function NoCustomInput({ onSamples }: { onSamples: () => void }) {
  const tInteractive = useTranslations("judge.interactive");
  return (
    <div className="flex items-start gap-3 rounded-xl bg-white/5 p-4 text-white motion-safe:animate-in motion-safe:fade-in" data-testid="no-custom-input">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-white/60" aria-hidden />
      <div className="min-w-0 space-y-2">
        <p className="text-sm font-semibold">{tInteractive("customTitle")}</p>
        <p className="text-[13px] leading-relaxed text-white/70">{tInteractive("customBody")}</p>
        <Button type="button" variant="outlineDark" size="pill" onClick={onSamples}>
          {tInteractive("customCta")}
        </Button>
      </div>
    </div>
  );
}

/* ── The strip ───────────────────────────────────────────────────────────── */

function stripMessage(
  results: SampleRunCaseResult[],
  t: Translate,
): { text: string; tone: "good" | "bad" | "muted"; firstBad: number } {
  const states = results.map(runCaseStateOf);
  const bad = results.filter(isBadRunCase).length;
  const firstBad = results.findIndex(isBadRunCase);
  if (bad === 0) {
    return states.every((state) => state === "match")
      ? { text: t("stripAllMatch", { n: results.length }), tone: "good", firstBad }
      : { text: t("stripUnchecked"), tone: "muted", firstBad };
  }
  if (bad === 1) return { text: t("stripOneBad", { n: firstBad + 1 }), tone: "bad", firstBad };
  if (bad === results.length) return { text: t("stripNoneMatch"), tone: "bad", firstBad };
  return { text: t("stripSomeBad", { bad, total: results.length }), tone: "bad", firstBad };
}

export function RunStrip({ runs }: { runs: CodeRuns }) {
  const t = useTranslations("judge.run");
  const { phase, result, error } = runs.samples;

  const shell = (children: ReactNode) => (
    <div className="shrink-0 border-t border-white/10 bg-neutral-950 px-3 py-2.5 sm:px-4" data-testid="run-strip">
      {children}
    </div>
  );

  if (phase === "slow") return shell(<SlowNote onRetry={runs.runSamples} />);

  if (phase === "failed") {
    return shell(
      <div className="flex items-center gap-2.5 text-xs">
        <p role="alert" className="min-w-0 flex-1 font-semibold text-red-300" data-testid="run-strip-line">
          {error}
        </p>
        <Button type="button" variant="inverse" size="mini" onClick={runs.runSamples}>
          {t("tryAgain")}
        </Button>
      </div>,
    );
  }

  if (phase === "running" || !result) {
    return shell(
      <>
        <div className={cn(styles.track, "mb-2 h-6 rounded-md bg-white/5")} aria-hidden>
          <div className={styles.sweep} />
        </div>
        <div role="status" data-testid="run-strip-line">
          {phase === "running" ? (
            <RunWaitClock
              since={runs.samples.startedAt}
              label={t("stripRunning")}
              running={(seconds) => t("runningSeconds", { seconds })}
              inLine={t("runningInLine")}
            />
          ) : (
            <p className="text-xs font-semibold text-white/80">{t("stripRunning")}</p>
          )}
        </div>
      </>,
    );
  }

  if (!result.compiled) {
    const first = firstLineOf(result.compileOutput);
    return shell(
      <div className="flex min-w-0 items-center gap-2.5 text-xs">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-400/15 text-red-300">
          <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        </span>
        <span className="shrink-0 font-semibold text-red-300" data-testid="run-strip-line">
          {t("doesNotCompile")}
        </span>
        {first ? (
          <span className="min-w-0 truncate font-mono text-white/60" data-testid="run-strip-compiler-line">
            {first}
          </span>
        ) : null}
        <Button
          type="button"
          variant="linkDark"
          size="mini"
          onClick={() => runs.setPanel("samples")}
          aria-controls={RUN_PANEL_ID}
          className="ml-auto shrink-0"
        >
          {t("compilerMessage")}
        </Button>
      </div>,
    );
  }

  const message = stripMessage(result.results, t);

  return shell(
    <>
      <div className="flex min-w-0 items-center gap-2 text-xs">
        {runs.stripOpen
          ? result.results.map((row, i) => {
              const state = runCaseStateOf(row);
              return (
                <Button
                  key={row.idx}
                  type="button"
                  variant={null}
                  size="mini"
                  onClick={() => runs.openSample(i)}
                  aria-controls={RUN_PANEL_ID}
                  className={cn("shrink-0", CASE_TONE[state])}
                >
                  <CaseIcon state={state} />
                  {t("sampleN", { n: i + 1 })}
                </Button>
              );
            })
          : null}
        <span
          className={cn(
            "min-w-0 truncate font-semibold",
            message.tone === "good" && "text-emerald-300",
            message.tone === "bad" && "text-red-300",
            message.tone === "muted" && "text-white/70",
          )}
          role="status"
          data-testid="run-strip-line"
        >
          {message.text}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {message.firstBad >= 0 && runs.panel !== "samples" ? (
            <Button
              type="button"
              variant="linkDark"
              size="mini"
              onClick={() => runs.openSample(message.firstBad)}
              aria-controls={RUN_PANEL_ID}
            >
              {t("compare")}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghostDark"
            size="miniIcon"
            onClick={runs.toggleStrip}
            aria-expanded={runs.stripOpen}
            aria-label={runs.stripOpen ? t("stripHide") : t("stripShow")}
          >
            {runs.stripOpen ? (
              <ChevronDown className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronUp className="h-4 w-4" aria-hidden />
            )}
          </Button>
        </span>
      </div>
      {runs.stripOpen ? <p className="mt-1.5 text-[11px] text-white/45">{t("stripNote")}</p> : null}
    </>,
  );
}
